import { useEffect, useState } from 'react';

/** How long a tab may be hidden before it gives its connection back. */
export const HIDDEN_GRACE_MS = 60_000;

/**
 * True once the tab has been hidden for `HIDDEN_GRACE_MS`.
 *
 * A browser allows six concurrent HTTP/1.1 connections per origin, and every
 * open dashboard tab holds three long-lived SSE streams against the API
 * origin. Two tabs therefore fill the pool on their own and every ordinary
 * request — the contacts list, the locations list — queues behind a stream that
 * never ends. Streams that stand down while their tab is in the background keep
 * the pool to the three the visible tab needs.
 *
 * The grace period matters: alt-tabbing for ten seconds should not cost a
 * reconnect and a fresh snapshot.
 */
export function useHiddenTabGrace(graceMs: number = HIDDEN_GRACE_MS): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    let grace: ReturnType<typeof setTimeout> | null = null;
    const sync = () => {
      if (grace) clearTimeout(grace);
      if (document.hidden) grace = setTimeout(() => setHidden(true), graceMs);
      else setHidden(false);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      if (grace) clearTimeout(grace);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [graceMs]);

  return hidden;
}
